import { PaymentMethodPicker, type PaymentMethod } from '../components/PaymentMethod';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { fullDate } from '../lib/format';
import { step } from '../telemetry';
import type { CurrentProvider, DnsRecord, Domain, DomainReport, User } from '../types';
import { VerifyBanner } from '../components/VerifyBanner';
/*
  ⚠️ DNS KARTLARI BURADAN ÇIXARILDI (2026-08-17) — İKİ YERDƏ LAZIM OLDU.
  Müştəri qarşılama ekranını bir dəfə keçəndən sonra yazıları bir daha
  görə bilmirdi. İndi eyni komponent Ayarlar bölməsində də işlədilir.
*/
import { Check, DnsGroups } from '../components/DnsRecords';

interface Props {
  user: User;
  onLogout: () => void;
  /** Poçt qutusu yaradılandan sonra App istifadəçini yenidən yükləsin. */
  onMailboxCreated: () => void;
}

/**
 * Qeydiyyatdan sonra görünən ekran — domen əlavəsindən ilk poçt qutusuna qədər.
 *
 * Mərhələ vəziyyətdən ÇIXARILIR, ayrıca saxlanılmır: istifadəçi səhifəni
 * yeniləsə və ya sabah qayıtsa, domenin bazadakı vəziyyəti hansı addımda
 * olduğunu onsuz da bilir. Ayrıca "hansı addımdayam" state saxlasaydıq, o,
 * bazadakı həqiqətlə uyğunsuz düşə bilərdi.
 */
type Stage =
  | { k: 'loading' }
  | { k: 'add'; domainId?: string; domain?: Domain }
  /**
   * `migrate` — domendə ARTIQ başqa poçt xidməti var.
   *
   * ⚠️ BU MƏRHƏLƏ OLMADAN REAL ZİYAN BAŞ VERİRDİ. Google Workspace
   * işlədən şirkətə heç nə soruşmadan «MX-i bura yönləndir» deyilirdi.
   * Tətbiq edən kimi:
   *   • gələn məktublar Google-a yox, bizə düşür — yeni qutu boşdur,
   *   • köhnə arxiv görünmür,
   *   • şirkətin poçtu günün ortasında dayanır.
   * İndi əvvəlcə xəbərdarlıq və köçürmə təklif olunur. Rəqiblərdən yalnız
   * Zoho və Hostinger bunu düzgün edir.
   */
  | { k: 'migrate'; domain: Domain; records: DnsRecord[]; provider: CurrentProvider }
  | { k: 'dns'; domain: Domain; records: DnsRecord[]; report: DomainReport | null }
  /**
   * `warn` — domen aktivləşdi, amma çatdırılma yazıları natamamdır.
   * Aktivləşmə üçün sahiblik + MX kifayətdir; SPF/DKIM olmadan poçt GƏLİR,
   * amma GEDƏN məktublar spama düşür. Bunu susaraq keçmək olmaz (docs/03).
   */
  | { k: 'mailbox'; domain: Domain; warn: string[]; mxOk: boolean }
  /**
   * `pay` — SON ADDIM. Qurulma bitdi, poçt hələ bağlıdır.
   *
   * ⚠️ SAHİBİN QƏRARI (2026-08-20): «ödəniş yapılmadan sistem açılmamalı».
   * Əvvəl qutu yaradılan kimi istifadəçi poçt ekranına düşürdü və orada
   * boş qutu ilə qarşılaşırdı — poçt onsuz da axmırdı, çünki kirayəçi
   * `unpaid` idi. Yəni sistem «açıq» görünürdü, amma işləmirdi; müştəri
   * səbəbini anlamırdı və dəstəyə yazırdı.
   *
   * İndi axının sonu açıqdır: qurulma → ÖDƏNİŞ → poçt. Ödəniş qeydə
   * alınan kimi `paid_until` qoyulur, status `active` olur və poçt
   * həmin an hər iki istiqamətdə açılır (Postfix bazanı canlı oxuyur).
   */
  | { k: 'pay'; domain: Domain; warn: string[]; mxOk: boolean };

function messageFor(code: string): string {
  switch (code) {
    case 'domain_in_use':
      return 'Bu domen artıq təsdiqlənib. Səhifəni yeniləyin.';
    case 'invalid_domain':
      return 'Domen adı düzgün deyil. Nümunə: sirketiniz.az';
    case 'already_exists':
      return 'Bu domen artıq əlavə edilib.';
    case 'limit_reached':
      return 'Paketinizin domen limiti dolub.';
    case 'exists':
      return 'Bu ünvan artıq mövcuddur.';
    case 'weak_password':
      return 'Şifrə ən azı 10 simvol olmalıdır.';
    case 'invalid_local_part':
      return 'Ünvanın adı yalnız hərf, rəqəm, nöqtə və defis ola bilər.';
    case 'domain_not_active':
      return 'Domen hələ aktiv deyil — əvvəlcə DNS yoxlamasını keçirin.';
    case 'quota_exceeded':
      return 'Paketinizin qutu limiti dolub.';
    default:
      return 'Əməliyyat alınmadı. Bir azdan yenidən yoxlayın.';
  }
}


export function Onboarding({ user, onLogout, onMailboxCreated }: Props): React.ReactElement {
  const [stage, setStage] = useState<Stage>({ k: 'loading' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [domainInput, setDomainInput] = useState('');
  /** «DNS-i başqasına göndər» sahəsi. */
  const [shareTo, setShareTo] = useState('');
  const [dnsSentTo, setDnsSentTo] = useState<string | null>(null);
  const [localPart, setLocalPart] = useState('');
  const [password, setPassword] = useState('');
  const [createdEmail, setCreatedEmail] = useState<string | null>(user.mailboxEmail ?? null);
  const [paymentMonths, setPaymentMonths] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const mailboxEmail = createdEmail || user.mailboxEmail;

  /** Domenin vəziyyətinə görə hansı mərhələdə olduğumuzu müəyyən edir. */
  const placeFor = useCallback(async (d: Domain): Promise<Stage> => {
    if (d.active) {
      const warn: string[] = [];
      if (!d.spfOk) warn.push('SPF');
      if (!d.dkimOk) warn.push('DKIM');
      // ⚠️ MX AYRICA DAŞINIR, `warn` siyahısına QARIŞDIRILMIR.
      // SPF/DKIM olmasa poçt gəlir (sadəcə göndərilən spama düşür);
      // MX olmasa poçt ÜMUMİYYƏTLƏ gəlmir. İki fərqli ağırlıqdır və
      // eyni cümlədə birləşdirilsə, ağırı yüngülün içində itir.
      /*
        ⚠️ QUTU ARTIQ VARSA VƏ ÖDƏNİŞ YOXDURSA — BİRBAŞA ÖDƏNİŞ ADDIMI.
        Səhifə yenilənəndə mərhələ bazadan yenidən hesablanır (bax faylın
        başındakı şərh). Bu şərt olmasa, ödəməmiş müştəri hər yeniləmədə
        «qutu yarat» formasını görərdi — halbuki qutusu var və ona lazım
        olan tək şey ödənişdir.
      */
      const odenisli = ['active', 'past_due', 'trial'].includes(user.tenantStatus);
      if (!odenisli && user.mailboxEmail) {
        return { k: 'pay', domain: d, warn, mxOk: d.mxOk };
      }
      return { k: 'mailbox', domain: d, warn, mxOk: d.mxOk };
    }
    const { records } = await api.domainDns(d.id);
    return { k: 'dns', domain: d, records, report: null };
  }, [user.tenantStatus, user.mailboxEmail]);

  useEffect(() => {
    let cancelled = false;
    api
      .domains()
      .then(async ({ domains }) => {
        if (cancelled) return;
        const d = domains[0];
        const next = d ? await placeFor(d) : { k: 'add' as const };
        if (!cancelled) setStage(next);
      })
      .catch(() => {
        if (!cancelled) setStage({ k: 'add' });
      });
    return () => {
      cancelled = true;
    };
  }, [placeFor]);

  function showDomain(domain: Domain): void {
    setDomainInput(domain.domain);
    setError(null);
    setStage({ k: 'add', domainId: domain.id, domain });
  }

  async function showDns(domain: Domain): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const { records } = await api.domainDns(domain.id);
      setStage({ k: 'dns', domain, records, report: null });
    } catch (err) {
      setError(messageFor(err instanceof ApiError ? err.code : 'unknown'));
    } finally {
      setBusy(false);
    }
  }

  function showMailbox(domain: Domain): void {
    const warn = [];
    if (!domain.spfOk) warn.push('SPF');
    if (!domain.dkimOk) warn.push('DKIM');
    setError(null);
    setStage({ k: 'mailbox', domain, warn, mxOk: domain.mxOk });
  }

  async function submitDomain(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (stage.k !== 'add') return;
    if (stage.domain && domainInput.trim() === stage.domain.domain) {
      await showDns(stage.domain);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      step(`Domen əlavə edilir: ${domainInput.trim()}`);
      const { id, currentProvider } = stage.k === 'add' && stage.domainId
        ? await api.correctDomain(stage.domainId, domainInput.trim())
        : await api.addDomain(domainInput.trim());
      setStage({ k: 'add', domainId: id });
      setShareTo('');
      setDnsSentTo(null);
      const { records } = await api.domainDns(id);
      const { domains } = await api.domains();
      const d = domains.find((x) => x.id === id);
      if (!d) return;

      // ⚠️ XƏBƏRDARLIQ YALNIZ POÇT VARSA. Yeni domendə (MX yoxdur) bu
      // ekranı göstərmək lazımsız qorxu yaradır və bir addım artırır.
      if (currentProvider.hasMail) {
        step(`Domendə mövcud poçt aşkarlandı: ${currentProvider.name ?? 'tanınmadı'}`, {
          mx: currentProvider.mx,
        });
        setStage({ k: 'migrate', domain: d, records, provider: currentProvider });
      } else {
        setStage({ k: 'dns', domain: d, records, report: null });
      }
    } catch (err) {
      setError(messageFor(err instanceof ApiError ? err.code : 'unknown'));
    } finally {
      setBusy(false);
    }
  }

  async function runVerify(): Promise<void> {
    if (stage.k !== 'dns') return;
    setBusy(true);
    setError(null);
    try {
      step(`DNS yoxlanılır: ${stage.domain.domain}`);
      const { report, activated } = await api.verifyDomain(stage.domain.id);

      if (activated) {
        const warn: string[] = [];
        if (!report.spf.ok) warn.push('SPF');
        if (!report.dkim.ok) warn.push('DKIM');
        step(`Domen aktivləşdi: ${stage.domain.domain}`, {
          catismayan: warn,
          mx: report.mx.ok,
        });
        setStage({
          k: 'mailbox',
          domain: { ...stage.domain, active: true, verified: true, mxOk: report.mx.ok,
            spfOk: report.spf.ok, dkimOk: report.dkim.ok, dmarcOk: report.dmarc.ok,
            checkedAt: new Date().toISOString() },
          warn,
          mxOk: report.mx.ok,
        });
      } else {
        step(`Domen hələ aktiv deyil: ${stage.domain.domain}`, {
          ownership: report.ownership.ok, mx: report.mx.ok, spf: report.spf.ok,
          dkim: report.dkim.ok, dmarc: report.dmarc.ok,
        });
        // ⚠️ `checkedAt` DƏ TƏZƏLƏNMƏLİDİR. Yalnız `report` yazsaq,
        // «Son yoxlama» vaxtı köhnə qalır və istifadəçi düyməni basdığı
        // halda vaxtın dəyişmədiyini görüb yoxlamanın işləmədiyini düşünür.
        setStage({
          ...stage,
          report,
          domain: { ...stage.domain, checkedAt: new Date().toISOString() },
        });
      }
    } catch (err) {
      setError(messageFor(err instanceof ApiError ? err.code : 'unknown'));
    } finally {
      setBusy(false);
    }
  }

  async function submitMailbox(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (stage.k !== 'mailbox') return;
    if (mailboxEmail) {
      setError(null);
      setStage({ ...stage, k: 'pay' });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      step(`Poçt qutusu yaradılır: ${localPart.trim()}@${stage.domain.domain}`);
      await api.createMailbox({
        domainId: stage.domain.id,
        localPart: localPart.trim().toLowerCase(),
        password,
        ...(user.displayName ? { displayName: user.displayName } : {}),
      });
      /*
        ⚠️ QUTU YARADILDI, AMMA POÇTA KEÇMİRİK — ÖDƏNİŞ ADDIMI QALIR.

        Əvvəl burada dərhal `onMailboxCreated()` çağırılırdı və istifadəçi
        poçt ekranına düşürdü. Orada isə onu boş qutu gözləyirdi: kirayəçi
        `unpaid` olduğu üçün nə məktub gəlirdi, nə də gedirdi. Sistem
        «açıq» görünürdü, halbuki işləmirdi.

        ⚠️ ÖDƏMİŞ MÜŞTƏRİ BU ADDIMI GÖRMÜR. Statusu artıq ödənişli olan
        kirayəçi (operator əl ilə açıbsa, və ya ödəniş artıq keçibsə)
        birbaşa poçta keçir — ona ödəniş ekranı göstərmək yanlış olardı.
      */
      const odenisli = ['active', 'past_due', 'trial'].includes(user.tenantStatus);
      if (odenisli) {
        step('Qutu yaradıldı, abunə aktivdir — poçt ekranına keçir');
        onMailboxCreated();
        return;
      }
      setCreatedEmail(`${localPart.trim().toLowerCase()}@${stage.domain.domain}`);
      setPassword('');
      step('Qutu yaradıldı — ödəniş addımına keçilir');
      setStage({ k: 'pay', domain: stage.domain, warn: stage.warn, mxOk: stage.mxOk });
      setBusy(false);
    } catch (err) {
      setError(messageFor(err instanceof ApiError ? err.code : 'unknown'));
      setBusy(false);
    }
  }

  /**
   * Tərəqqi zolağında hansı addımın işıqlandığı.
   *
   * ⚠️ HƏR MƏRHƏLƏ AÇIQ SADALANIR — «qalan hər şey 2» yazmaq OLMAZ.
   * Əvvəl `stage.k === 'add' ? 0 : stage.k === 'dns' ? 1 : 2` idi və
   * `migrate` mərhələsi əlavə olunanda o, səssizcə 2-yə düşdü: müştəri
   * hələ DNS-i qurmamış «DNS yazıları ✓» görürdü. Yeni mərhələ əlavə
   * edən adam bu sətri yenidən oxumağa məcbur olsun deyə açıq yazılır.
   */
  const stepIndex =
    stage.k === 'add'
      ? 0
      : stage.k === 'migrate' || stage.k === 'dns'
        ? 1
        : stage.k === 'mailbox'
          ? 2
          : 3;

  return (
    <div className="onboard">
      <header className="onboard-bar">
        <div className="login-brand">
          <span className="login-mark">M</span>
          <span className="login-name">Mailora</span>
        </div>
        <button className="btn-text" onClick={onLogout}>
          Çıxış
        </button>
      </header>

      <main className="onboard-main">
        <h1>Xoş gəldiniz{user.displayName ? `, ${user.displayName}` : ''}</h1>

        {/* ⚠️ TƏSDİQ ZOLAĞI BURADA ƏN VACİBDİR — qeydiyyatdan dərhal
            sonra istifadəçi məhz bu ekranı görür və məktubu məhz indi
            gözləyir. Əvvəl zolaq yalnız poçt ekranında idi, yəni yeni
            istifadəçi onu heç vaxt görmürdü. */}
        <VerifyBanner kind={user.kind} emailVerified={user.emailVerified} />

        <ol className="onboard-rail">
          {/*
            ⚠️ DÖRDÜNCÜ ADDIM «Ödəniş» — SONUNCU VƏ MƏCBURİ.
            Müştəri ilk ekrandan görür ki, axının sonunda ödəniş var.
            Gizlətmək və qutu yaradıldıqdan sonra qarşısına çıxarmaq
            aldadıcı olardı; burada isə gözlənti əvvəlcədən qurulur.
          */}
          {['Domen', 'DNS yazıları', 'Poçt qutusu', 'Ödəniş'].map((t, i) => (
            <li
              key={t}
              aria-current={i === stepIndex ? 'step' : undefined}
              className={`rail-step ${i < stepIndex ? 'is-done' : i === stepIndex ? 'is-active' : 'is-todo'}`}
            >
              <span className="rail-num">{i < stepIndex ? '✓' : i + 1}</span>
              {t}
            </li>
          ))}
        </ol>

        {error ? (
          <p className="login-error" role="alert">
            {error}
          </p>
        ) : null}

        {stage.k === 'loading' ? <div className="spinner" aria-label="Yüklənir" /> : null}

        {stage.k === 'add' ? (
          <form className="onboard-panel" onSubmit={submitDomain}>
            <h2>{stage.domain?.verified || stage.domain?.active ? 'Domeniniz' : stage.domainId ? 'Domeninizi düzəldin' : 'Domeninizi əlavə edin'}</h2>
            <p className="onboard-lead">
              Poçt ünvanlarınız bu domendə olacaq — məsələn{' '}
              <code>ad@{domainInput.trim() || 'sirketiniz.az'}</code>. Domenin sizə
              aid olduğunu bir DNS yazısı ilə təsdiqləyəcəyik.
            </p>
            <label className="field">
              <span>Domen</span>
              <input
                required
                autoFocus
                placeholder="sirketiniz.az"
                value={domainInput}
                readOnly={stage.domain?.verified || stage.domain?.active}
                disabled={busy}
                onChange={(e) => setDomainInput(e.target.value)}
              />
            </label>
            {stage.domain?.verified || stage.domain?.active ? (
              <p className="onboard-note">Domen artıq təsdiqlənib və burada dəyişdirilə bilməz.</p>
            ) : null}
            <div className="onboard-actions">
              <button type="submit" className="btn-primary" disabled={busy || !domainInput.trim()}>
                {busy ? 'Saxlanılır…' : 'İrəli — DNS yazıları →'}
              </button>
            </div>
          </form>
        ) : null}

        {stage.k === 'migrate' ? (
          <div className="onboard-panel">
            <h2>
              <code>{stage.domain.domain}</code> domenində artıq poçt var
            </h2>

            <div className="banner is-warn" role="status">
              <span>
                {stage.provider.name ? (
                  <>
                    Bu domenin poçtu hazırda <strong>{stage.provider.name}</strong>{' '}
                    üzərindədir.
                  </>
                ) : (
                  <>Bu domendə hazırda başqa bir poçt xidməti işləyir.</>
                )}
              </span>
            </div>

            {/*
              ⚠️ NƏTİCƏ AÇIQ VƏ SƏRT YAZILIR. «MX-i dəyişmək tövsiyə
              olunmur» kimi yumşaq cümlə oxunmur və müştəri davam edir.
              Nə itirəcəyini dəqiq bilməlidir.
            */}
            <p className="onboard-lead">
              MX yazısını dəyişən kimi gələn məktublar köhnə sistemə YOX, bizə
              düşəcək. Köhnə qutulardakı məktublar isə orada qalır — özü köçmür.
              Ona görə MX ƏN SONDA dəyişilir.
            </p>

            {/*
              ⚠️ SIRA NÖMRƏLƏNMİŞ GÖSTƏRİLİR. «Diqqətli olun» demək kifayət
              etmir — müştəri nə edəcəyini dəqiq bilməlidir. Bu, Google və
              Microsoft sihirbazlarındakı sıradır.
            */}
            <ol className="plan">
              <li>
                <strong>Sahiblik yazısını əlavə edin</strong> — bu, heç nəyi
                dəyişmir, poçtunuz olduğu kimi işləməyə davam edir.
              </li>
              <li>
                <strong>Poçt qutularınızı yaradın</strong> — hələ boşdurlar.
              </li>
              <li>
                <strong>Köhnə məktubları köçürün</strong> — paneldəki «Köçürmə»
                bölməsindən, {stage.provider.name ?? 'mövcud xidmətinizdən'}.
              </li>
              <li>
                <strong>Ən sonda MX-i dəyişin</strong> — bu andan yeni məktublar
                bizə gəlir və köçürülmüş arxiv onsuz da yerindədir.
              </li>
            </ol>

            <dl className="mx-found">
              <dt>Tapılan MX yazıları</dt>
              <dd>
                {stage.provider.mx.map((h: string) => (
                  <code key={h}>{h}</code>
                ))}
              </dd>
            </dl>

            <div className="onboard-actions">
            <button type="button" className="btn-secondary" disabled={busy}
              onClick={() => showDomain(stage.domain)}>← Geri — Domen</button>
            <button
              className="btn-primary"
              disabled={busy}
              onClick={() => {
                // ⚠️ TAPILAN PROVAYDER YADDA SAXLANILIR.
                // Köçürmə ekranı qutu yaradıldıqdan SONRA açılır; o vaxta
                // qədər bu məlumat itməsin deyə brauzerdə saxlanılır və
                // orada siyahı öz-özünə seçilmiş gəlir — bir addım az.
                if (stage.k === 'migrate' && stage.provider.migrationKey) {
                  try {
                    localStorage.setItem('mailora.migration.hint', stage.provider.migrationKey);
                  } catch {
                    // Şəxsi rejimdə localStorage qadağandır — əhəmiyyətsizdir,
                    // müştəri sadəcə siyahıdan özü seçəcək.
                  }
                }
                step('Köçürmə planı qəbul edildi', { provider: stage.provider.migrationKey });
                setStage({ k: 'dns', domain: stage.domain, records: stage.records, report: null });
              }}
            >
              Anladım, irəli →
            </button>
            </div>

            {/*
              ⚠️ TƏXMİN OLDUĞU AÇIQ DEYİLİR. MX-ə baxıb xidməti tanımaq
              həmişə dəqiq deyil — spam süzgəci arxasındakı server görünmür.
              Səhv təxmində müştəri sistemə inamını itirməsin deyə, bunun
              bir təxmin olduğunu özümüz deyirik.
            */}
            <p className="onboard-note">
              Bu, DNS-dəki MX yazılarına əsaslanan təxmindir. Domeni yeni
              almısınızsa və ya poçt işlətmirsinizsə, sadəcə davam edin.
            </p>
          </div>
        ) : null}

        {stage.k === 'dns' ? (
          <div className="onboard-panel">
            <h2>
              <code>{stage.domain.domain}</code> üçün DNS yazıları
            </h2>
            <p className="onboard-lead">
              Bunları domeninizin idarə panelində (registrar və ya Cloudflare)
              əlavə edin, sonra «Yoxla» düyməsini basın. DNS-in yayılması adətən
              bir neçə dəqiqə çəkir, bəzən bir saata qədər.
            </p>

            {/*
              DNS YAZILARINI BAŞQASINA GÖNDƏR.
              ⚠️ BU, FUNNEL-İN ƏN BÖYÜK TIXACIDIR. Qeydiyyatdan keçən adam
              (direktor) çox vaxt registrar parolunu tutan adam deyil.
              Onsuz o adam bu ekranda ilişib qalır və heç vaxt qayıtmır.
              Rəqiblərin heç birində bu düymə yoxdur.
            */}
            <div className="dns-share">
              {dnsSentTo ? (
                <p className="onboard-note is-ok">
                  Yazılar <strong>{dnsSentTo}</strong> ünvanına göndərildi.
                </p>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (stage.k !== 'dns' || !shareTo.trim()) return;
                    setBusy(true);
                    setError(null);
                    void api
                      .emailDnsRecords(stage.domain.id, shareTo.trim())
                      .then((r) => setDnsSentTo(r.sentTo))
                      .catch((err: unknown) =>
                        setError(
                          err instanceof ApiError && err.code === 'too_many_attempts'
                            ? 'Çox cəhd edildi — bir saatdan sonra yenidən yoxlayın.'
                            : 'Məktub göndərilmədi. Ünvanı yoxlayın.',
                        ),
                      )
                      .finally(() => setBusy(false));
                  }}
                >
                  <label className="field">
                    <span>DNS-i quran başqa şəxsdirsə, yazıları ona göndərin</span>
                    <div className="dns-share-row">
                      <input
                        type="email"
                        placeholder="it@sirketiniz.az"
                        value={shareTo}
                        onChange={(e) => setShareTo(e.target.value)}
                      />
                      <button className="btn-secondary" type="submit" disabled={busy || !shareTo.trim()}>
                        Göndər
                      </button>
                    </div>
                  </label>
                </form>
              )}
            </div>

            {/*
              Sahələr ETİKETLƏNİR. DNS panelində forma «Type / Name / Content»
              adlanır və hansı dəyərin hara getdiyi aydın olmalıdır.

              ⚠️ Prioritet AYRICA sahədir. `10 mx1.mailora.az` şəklində
              birlikdə yazılsa, Cloudflare onu Content sahəsində qəbul etmir
              və MX yazısı səhv qurulur.
            */}
            <DnsGroups records={stage.records} />

            {stage.report ? (
              <ul className="chk-list">
                <Check label="Sahiblik" r={stage.report.ownership} />
                <Check label="MX" r={stage.report.mx} />
                <Check label="SPF" r={stage.report.spf} />
                <Check label="DKIM" r={stage.report.dkim} />
                <Check label="DMARC" r={stage.report.dmarc} />
              </ul>
            ) : null}

            <div className="dns-verify">
              <div className="onboard-actions">
                <button type="button" className="btn-secondary" disabled={busy}
                  onClick={() => showDomain(stage.domain)}>← Geri — Domen</button>
                {stage.domain.active ? (
                  <button type="button" className="btn-secondary" onClick={runVerify} disabled={busy}>
                    {busy ? 'Yoxlanılır…' : 'Yenidən yoxla'}
                  </button>
                ) : null}
                <button type="button" className="btn-primary" disabled={busy}
                  onClick={() => stage.domain.active ? showMailbox(stage.domain) : void runVerify()}>
                  {busy ? 'Yoxlanılır…' : stage.domain.active ? 'İrəli — Poçt qutusu →' : 'Yoxla və irəli →'}
                </button>
              </div>
              {/*
                SON YOXLAMA VAXTI.
                ⚠️ `dns_checked_at` bazada ÇOXDAN saxlanılırdı və API-də də
                gəlirdi (`Domain.checkedAt`), amma heç yerdə göstərilmirdi.
                Vaxt olmadan istifadəçi ekrandakı «✗» nişanının indiki
                vəziyyəti, yoxsa 10 dəqiqə əvvəlki nəticəni göstərdiyini
                bilmir — və ya boş yerə gözləyir, ya da DNS-i yenidən
                qurmağa başlayır. Yandex hər status kartında «Last checked
                on …» yazır.
              */}
              {stage.domain.checkedAt && (
                <span className="dns-checked">
                  Son yoxlama: {fullDate(stage.domain.checkedAt)}
                </span>
              )}
            </div>
            {stage.report && !stage.report.canActivate ? (
              <p className="onboard-note">
                Hələ hamısı hazır deyil. Yazıları əlavə etdikdən sonra bir neçə
                dəqiqə gözləyib yenidən yoxlayın.
              </p>
            ) : null}
          </div>
        ) : null}

        {stage.k === 'mailbox' ? (
          <form className="onboard-panel" onSubmit={submitMailbox}>
            <h2>{mailboxEmail ? 'Poçt qutunuz hazırdır' : 'İlk poçt qutunuzu yaradın'}</h2>
            {mailboxEmail ? (
              <p className="onboard-lead"><strong>{mailboxEmail}</strong> yaradılıb. Ödəniş addımına davam edə bilərsiniz.</p>
            ) : <p className="onboard-lead">
              <code>{stage.domain.domain}</code> təsdiqləndi. İndi öz ünvanınızı
              yaradın — bu qutu ilə panelə və istənilən poçt proqramına
              (Outlook, iPhone, Thunderbird) girə biləcəksiniz.
            </p>}

            {/*
              MX HƏLƏ DƏYİŞMƏYİB — AYRICA VƏ DAHA GÜCLÜ XƏBƏRDARLIQ.
              ⚠️ Bu, nasazlıq DEYİL, düzgün sıradır: qutu yaradılır,
              məktublar köçürülür, MX ƏN SONDA dəyişilir. Amma müştəri
              bunu bilməsə, «qutu yaratdım, məktub gəlmir» deyib dəstəyə
              yazır. Ona görə vəziyyət açıq izah olunur.
            */}
            {!stage.mxOk ? (
              <div className="banner is-info" role="status">
                <span>
                  <strong>Məktublar hələ köhnə sisteminizə gəlir</strong> — MX
                  yazısı dəyişməyib. Bu, hazırda DÜZGÜNDÜR: əvvəlcə qutunu
                  yaradın, köhnə məktubları köçürün, MX-i ən sonda dəyişin.
                  Beləliklə heç nə itmir.
                </span>
              </div>
            ) : null}

            {stage.warn.length > 0 ? (
              <p className="onboard-warn" role="status">
                <strong>{stage.warn.join(' və ')}</strong> yazınız hələ DNS-də
                görünmür. Poçt qəbulu işləyəcək, amma{' '}
                <strong>göndərdiyiniz məktublar spama düşə bilər</strong>. Qutunu
                indi yarada bilərsiniz — yazıları sonra əlavə edib yenidən
                yoxlamaq kifayətdir.
              </p>
            ) : null}

            {!mailboxEmail ? <>
            <label className="field">
              <span>Ünvan</span>
              <div className="field-addon">
                <input
                  required
                  autoFocus
                  placeholder="ad"
                  value={localPart}
                  onChange={(e) => setLocalPart(e.target.value)}
                />
                <span className="addon">@{stage.domain.domain}</span>
              </div>
            </label>

            <label className="field">
              <span>Şifrə</span>
              <input
                required
                type="password"
                minLength={10}
                placeholder="ən azı 10 simvol"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>

            </> : null}
            <div className="onboard-actions">
              <button type="button" className="btn-secondary" disabled={busy}
                onClick={() => void showDns(stage.domain)}>← Geri — DNS yazıları</button>
              <button
                type="submit"
                className="btn-primary"
                disabled={busy || (!mailboxEmail && (!localPart.trim() || password.length < 10))}
              >
                {busy ? 'Yaradılır…' : mailboxEmail ? 'İrəli — Ödəniş →' : 'Qutunu yarat və irəli →'}
              </button>
            </div>
          </form>
        ) : null}

        {stage.k === 'pay' ? (
          <PayStep domain={stage.domain.domain} months={paymentMonths} onMonthsChange={setPaymentMonths}
            onBack={() => showMailbox(stage.domain)} method={paymentMethod} onMethodChange={setPaymentMethod} />
        ) : null}
      </main>
    </div>
  );
}

/**
 * SON ADDIM — ödəniş.
 *
 * ⚠️ AYRICA KOMPONENT, ÇÜNKİ ÖZ MƏLUMATINI ÖZÜ YÜKLƏYİR. Onboarding-in
 * qalan mərhələləri domen məlumatı ilə işləyir; burada isə məbləğ
 * lazımdır və o, SERVERDƏ hesablanır (müştəridən qiymət qəbul etmirik).
 *
 * ⚠️ «Abunə» ekranını təkrar yazmırıq, sadəcə eyni ucları çağırırıq:
 * məbləğ və ödəniş məntiqi bir yerdədir (billing/payments.ts).
 */
function PayStep({ domain, months, onMonthsChange, onBack, method, onMethodChange }: {
  domain: string;
  months: number;
  onMonthsChange: (months: number) => void;
  onBack: () => void;
  method: PaymentMethod;
  onMethodChange: (method: PaymentMethod) => void;
}): React.ReactElement {
  const [sum, setSum] = useState<{ totalMinor: number; currency: string; cardEnabled?: boolean } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .billing()
      .then((r) => {
        if (!cancelled) setSum(r.billing);
      })
      .catch(() => {
        if (!cancelled) setErr('Məbləğ yüklənmədi. Səhifəni yeniləyin.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const cem = sum ? sum.totalMinor * months : 0;
  const pul = (m: number): string =>
    `${(m / 100).toFixed(2)} ${sum?.currency ?? 'AZN'}`;

  async function ode(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const { url } = await api.startPayment(months, method);
      window.location.assign(url);
      // ⚠️ `setBusy(false)` YOXDUR — yönləndirmə başlayıb. Vəziyyəti geri
      // qaytarsaq, səhifə ölməzdən əvvəl düymə bir anlıq aktivləşir və
      // həmin anda basmaq İKİNCİ ödəniş sətri yaradır.
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'unknown';
      setErr(
        code === 'not_configured'
          ? 'Kartla ödəniş hazırda əlçatan deyil. Bizə yazın — faktura göndərək.'
          : code === 'too_many_attempts'
            ? 'Ard-arda çox cəhd oldu. Bir neçə dəqiqədən sonra yenidən yoxlayın.'
            : 'Ödəniş səhifəsi açılmadı. Kartınızdan heç nə çıxılmayıb — bir azdan yenidən cəhd edin.',
      );
      setBusy(false);
    }
  }

  return (
    <section className="onboard-card pay-step">
      <button type="button" className="btn-secondary" disabled={busy} onClick={onBack}>
        ← Geri — Poçt qutusu
      </button>
      <h2>Son addım — abunəni aktivləşdirin</h2>

      {/*
        ⚠️ VƏZİYYƏT AÇIQ DEYİLİR. Müştəri qurulmanı bitirib və poçtun niyə
        hələ işləmədiyini bilməlidir. «Demək» burada dürüstlükdür: qutu
        hazırdır, DNS hazırdır, tək qalan ödənişdir.
      */}
      <p className="onboard-note">
        <strong>{domain}</strong> hazırdır: domen bağlanıb, DNS qurulub, poçt
        qutusu yaradılıb. Poçtun işə düşməsi üçün yalnız abunə qalıb.
      </p>
      <p className="onboard-note">
        Ödəniş qeydə alınan kimi məktublar <strong>dərhal</strong> gəlib-getməyə
        başlayır — yenidən quraşdırma tələb olunmur.
      </p>

      {err ? (
        <p className="login-error" role="alert">
          {err}
        </p>
      ) : null}

      {sum === null ? (
        <div className="spinner" aria-label="Yüklənir" />
      ) : (
        <>
          <div className="pay-months" role="group" aria-label="Ödəniş müddəti">
            {[1, 3, 6, 12].map((m) => (
              <button
                key={m}
                type="button"
                className={`pay-month ${m === months ? 'is-on' : ''}`}
                aria-pressed={m === months}
                disabled={busy}
                onClick={() => onMonthsChange(m)}
              >
                {m} ay
              </button>
            ))}
          </div>

          {sum.cardEnabled !== false ? (
            <>
            <PaymentMethodPicker value={method} onChange={onMethodChange} disabled={busy} />
            <button
              type="button"
              className="btn-primary pay-cta"
              disabled={busy}
              onClick={() => void ode()}
            >
              {busy ? 'Yönləndirilir…' : `${method === 'card' ? 'Kartla ödə' : 'Apple Pay / Google Pay ilə ödə'} — ${pul(cem)}`}
            </button>
            </>
          ) : (
            <p className="onboard-note">
              Kartla ödəniş hazırda əlçatan deyil. Bizə yazın — faktura ilə
              ödəyə bilərsiniz.
            </p>
          )}

          <p className="onboard-note">
            Ödəniş Yığım ödəniş sistemi üzərindən aparılır. Kart məlumatlarınız
            bizim serverə düşmür. ƏDV yoxdur — göstərilən məbləğ yekundur.
          </p>
        </>
      )}
    </section>
  );
}
